import React from 'react';
import {
    Alert,
    Avatar,
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Skeleton,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PostAddIcon from '@mui/icons-material/PostAdd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {useTranslation} from 'react-i18next';
import type {TableComponentMobileProps} from '../../../hooks/useEmployeeHandlers';
import {useTableMobile} from '../../../hooks/useEmployeeHandlers';
import {getAvatarColors, getInitials} from '../../../utils/avatarUtils';

const TableComponentMobile: React.FC<TableComponentMobileProps> = ({
                                                                       employees,
                                                                       loading,
                                                                       error,
                                                                       selected,
                                                                       page = 1,
                                                                       rowsPerPage = 10,
                                                                       searchQuery = '',
                                                                       handleClick,
                                                                       isSelected,
                                                                       handleEdit,
                                                                       handleView,
                                                                       handleAddContract
                                                                   }) => {
    const {t} = useTranslation();
    const {
        anchorEl,
        selectedRowId,
        open,
        paginatedEmployees,
        handleMenuClick,
        handleMenuClose,
        handleMenuAction,
    } = useTableMobile({employees, page, rowsPerPage});

    if (loading) {
        return (
            <Stack spacing={2}>
                {Array.from({length: 5}).map((_, i) => (
                    <Card key={`skeleton-${i}`} variant="outlined" sx={{backgroundColor: '#fff'}}>
                        <CardContent>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                                <Skeleton variant="rectangular" height={24} width={100}/>
                                <Skeleton variant="circular" width={24} height={24}/>
                            </Box>
                            <Skeleton variant="text" width="70%" height={24} sx={{mb: 0.5}}/>
                            <Skeleton variant="text" width="90%" height={20} sx={{mb: 1}}/>
                            <Box sx={{display: 'flex', gap: 1, mt: 1.5}}>
                                <Skeleton variant="rounded" width={90} height={24}/>
                                <Skeleton variant="rounded" width={110} height={24}/>
                            </Box>
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        );
    }



    if (employees.length === 0) {
        return (
            <Box sx={{textAlign: 'center', py: 6}}>
                <PersonOffIcon sx={{fontSize: 48, color: 'text.disabled', mb: 1}}/>
                {searchQuery.trim() ? (
                    <>
                        <Typography variant="body1" color="text.secondary">
                            {t('employee.table.noResultsFor', {query: searchQuery})}</Typography>
                        <Typography variant="body2" color="text.disabled">{t('employee.table.tryDifferentSearch')}</Typography>
                    </>
                ) : (
                    <>
                        <Typography variant="body1" color="text.secondary">{t('employee.table.noEmployeesFound')}</Typography>
                        <Typography variant="body2" color="text.disabled">{t('employee.table.tryAdjustingOrAdd')}</Typography>
                    </>
                )}
            </Box>
        );
    }

    return (
        <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {paginatedEmployees.map((row) => {
                const isItemSelected = isSelected(row.id);
                const isActive = row.status.toLowerCase() === 'active';
                const avatarColors = getAvatarColors(`${row.firstName}${row.lastName}${row.id}`);

                return (
                    <Card
                        key={row.id}
                        variant="outlined"
                        role="article"
                        aria-label={t('employee.table.ariaEmployee', {firstName: row.firstName, lastName: row.lastName})}
                        sx={{
                            borderRadius: 1,
                            backgroundColor: isItemSelected ? 'action.selected' : '#fff',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:active': {
                                transform: 'scale(0.98)',
                            },
                            '&:hover': {
                                boxShadow: 2,
                            }
                        }}
                        onClick={(event: any) => handleClick(event, row.id)}
                    >
                        <CardContent sx={{pb: '12px !important'}}>
                            {/* Top row: Checkbox, Avatar, Name/Code, Chip, Menu */}
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1}}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, minWidth: 0}}>
                                    <Checkbox
                                        color="primary"
                                        checked={isItemSelected}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => handleClick({} as any, row.id)}
                                        aria-label={t('employee.table.ariaSelectEmployee', {firstName: row.firstName, lastName: row.lastName})}
                                        sx={{
                                            flexShrink: 0,
                                            p: 0.5,
                                        }}
                                    />
                                    <Avatar sx={{
                                        width: 40,
                                        height: 40,
                                        bgcolor: avatarColors.bg,
                                        color: avatarColors.color,
                                        fontWeight: 600,
                                        flexShrink: 0,
                                    }}>
                                        {getInitials(row.firstName, row.lastName)}
                                    </Avatar>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        noWrap
                                        sx={{textAlign: 'left'}}
                                    >
                                        {row.firstName} {row.lastName}
                                    </Typography>
                                </Box>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0}}>
                                    <Chip
                                        label={row.status}
                                        size="small"
                                        color={isActive ? 'success' : 'error'}
                                        sx={{fontWeight: 500}}
                                    />
                                <Box onClick={(e) => e.stopPropagation()}>
                                    <Tooltip title={t('hr.branches.mobile.actionsTooltip')}>
                                        <IconButton
                                            size="small"
                                            aria-label={t('employee.table.ariaActionsFor', {firstName: row.firstName, lastName: row.lastName})}
                                            onClick={(e) => handleMenuClick(e, row.id)}
                                            sx={{
                                                bgcolor: 'action.hover',
                                                '&:hover': {bgcolor: 'action.selected'}
                                            }}
                                        >
                                            <MoreVertIcon/>
                                        </IconButton>
                                    </Tooltip>
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={open && selectedRowId === row.id}
                                        onClose={handleMenuClose}
                                        slotProps={{
                                            paper: {
                                                style: {
                                                    maxHeight: 48 * 4.5,
                                                    width: '20ch',
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem onClick={() => handleMenuAction(() => handleEdit(row.id))}>
                                            <ListItemIcon><EditIcon fontSize="small" color="primary"/></ListItemIcon>
                                            <ListItemText>{t('common.actions.edit')}</ListItemText>
                                        </MenuItem>
                                        <MenuItem onClick={() => handleMenuAction(() => handleView(row.id))}>
                                            <ListItemIcon><VisibilityIcon fontSize="small" color="info"/></ListItemIcon>
                                            <ListItemText>{t('employee.table.viewContracts')}</ListItemText>
                                        </MenuItem>
                                        <Divider/>
                                        <MenuItem
                                            onClick={() => handleMenuAction(() => handleAddContract(row.id))}
                                            disabled={row.hasActiveContract}
                                        >
                                            <ListItemIcon><PostAddIcon fontSize="small"
                                                                       color={row.hasActiveContract ? 'disabled' : 'success'}/></ListItemIcon>
                                            <ListItemText>{t('employee.table.addContract')}</ListItemText>
                                        </MenuItem>
                                    </Menu>
                                </Box>
                                </Box>
                            </Box>

                            <Divider sx={{mb: 1.5}}/>

                            {/* Detail rows */}
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <WorkOutlineIcon sx={{fontSize: 18, color: 'text.secondary'}}/>
                                    <Typography variant="body2" sx={{textAlign: 'left'}}>
                                        {row.employmentType || '-'}
                                    </Typography>
                                </Box>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <LocationOnOutlinedIcon sx={{fontSize: 18, color: 'text.secondary'}}/>
                                    <Typography variant="body2" sx={{textAlign: 'left'}}>
                                        {row.branch || '-'}
                                    </Typography>
                                </Box>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <EmailIcon sx={{fontSize: 18, color: 'text.secondary'}}/>
                                    {row.email ? (
                                        <Typography
                                            variant="body2"
                                            noWrap
                                            sx={{textAlign: 'left', minWidth: 0}}
                                        >
                                            {row.email}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.disabled" fontStyle="italic">
                                            {t('employee.table.noEmailProvided')}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <PhoneIcon sx={{fontSize: 18, color: 'text.secondary'}}/>
                                    {row.phone ? (
                                        <Typography variant="body2" sx={{textAlign: 'left'}}>
                                            (+383) {row.phone}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="text.disabled" fontStyle="italic">
                                            {t('employee.table.noPhoneProvided')}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
};

export default TableComponentMobile;

